import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaSearch, FaTimes } from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner';

const SearchableDropdown = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Select an option...',
    disabled = false,
    loading = false,
    className = '',
    style = {},
    size = 'medium', // 'small', 'medium', 'large'
    searchable = true,
    clearable = true,
    multiple = false,
    selectedValues = [],
    onSelectionChange,
    optionLabel = 'name',
    optionValue = 'id',
    groupBy = null,
    sortOptions = true,
    typeable = true, // New prop to enable typing in trigger
    disableClickOutside = false // New prop to disable click outside behavior
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Initialize input value when value prop changes
    useEffect(() => {
        if (value) {
            const selectedOption = options.find(opt => opt[optionValue] === value);
            if (selectedOption) {
                setInputValue(selectedOption[optionLabel]);
            }
        } else {
            setInputValue('');
        }
    }, [value, options, optionLabel, optionValue]);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (disableClickOutside) return; // Don't add click outside handler if disabled
        
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [disableClickOutside]);

    // Handle dropdown toggle
    const handleToggle = (e) => {
        if (disabled) return;
        
        // Prevent the click from bubbling up if it's on the input field
        if (e.target.classList.contains('dropdown-input')) {
            return;
        }
        
        setIsOpen(!isOpen);
        if (!isOpen) {
            setSearchTerm('');
            setFocusedIndex(-1);
            // Focus input after a short delay to ensure dropdown is open
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    };

    // Handle input change for typeable mode
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setSearchTerm(newValue);
        setIsOpen(true);
        setFocusedIndex(-1);
    };

    // Handle input click to prevent dropdown toggle when clicking on input
    const handleInputClick = (e) => {
        e.stopPropagation();
        if (!isOpen) {
            setIsOpen(true);
            setSearchTerm('');
            setFocusedIndex(-1);
        }
    };

    // Handle option selection
    const handleOptionSelect = (option) => {
        if (multiple) {
            const newValues = selectedValues.includes(option[optionValue])
                ? selectedValues.filter(v => v !== option[optionValue])
                : [...selectedValues, option[optionValue]];
            onSelectionChange?.(newValues);
        } else {
            onChange?.(option[optionValue]);
            setInputValue(option[optionLabel]);
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    // Handle select all functionality
    const handleSelectAll = () => {
        if (multiple) {
            const allOptionValues = sortedOptions.map(option => option[optionValue]);
            onSelectionChange?.(allOptionValues);
        }
    };

    // Handle deselect all functionality
    const handleDeselectAll = () => {
        if (multiple) {
            onSelectionChange?.([]);
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => 
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => 
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
                    handleOptionSelect(filteredOptions[focusedIndex]);
                } else if (searchTerm && filteredOptions.length > 0) {
                    // Select first option if typing and no option focused
                    handleOptionSelect(filteredOptions[0]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setFocusedIndex(-1);
                break;
        }
    };

    // Clear selection
    const handleClear = (e) => {
        e.stopPropagation();
        if (multiple) {
            onSelectionChange?.([]);
        } else {
            onChange?.('');
            setInputValue('');
        }
        setSearchTerm('');
    };

    // Filter options based on search term
    const filteredOptions = options.filter(option => {
        if (!searchTerm) return true;
        const label = String(option[optionLabel] || '').toLowerCase();
        const value = String(option[optionValue] || '').toLowerCase();
        return label.includes(searchTerm.toLowerCase()) || 
               value.includes(searchTerm.toLowerCase());
    });

    // Sort options if enabled
    const sortedOptions = sortOptions 
        ? [...filteredOptions].sort((a, b) => 
            String(a[optionLabel]).localeCompare(String(b[optionLabel]))
        ) : filteredOptions;

    // Get size classes
    const sizeClasses = {
        small: 'dropdown-small',
        medium: 'dropdown-medium',
        large: 'dropdown-large'
    };

    // Get current display value
    const getDisplayValue = () => {
        if (multiple && selectedValues.length > 0) {
            const selectedLabels = selectedValues.map(val => {
                const option = options.find(opt => opt[optionValue] === val);
                return option ? option[optionLabel] : val;
            });
            return selectedLabels.join(', ');
        }
        return inputValue || placeholder;
    };

    return (
        <div 
            className={`searchable-dropdown ${sizeClasses[size]} ${className} ${disabled ? 'disabled' : ''}`}
            style={style}
            ref={dropdownRef}
        >
            <div 
                className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
                onClick={handleToggle}
            >
                {typeable ? (
                    <input
                        ref={inputRef}
                        type="text"
                        className="dropdown-input"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        disabled={disabled}
                        onClick={handleInputClick}
                    />
                ) : (
                    <span className="dropdown-value">
                        {getDisplayValue()}
                    </span>
                )}
                
                <div className="dropdown-controls">
                    {clearable && (value || (multiple && selectedValues.length > 0)) && (
                        <button
                            className="clear-button"
                            onClick={handleClear}
                            type="button"
                            title="Clear selection"
                        >
                            <FaTimes />
                        </button>
                    )}
                    <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>
                        <FaChevronDown />
                    </span>
                </div>
            </div>

            {isOpen && (
                <div className="dropdown-menu">
                    {loading && (
                        <div className="dropdown-loading">
                            <LoadingSpinner text="Loading..." variant="inline" size="small" />
                        </div>
                    )}
                    
                    {!loading && sortedOptions.length === 0 && (
                        <div className="dropdown-no-options">
                            <span>{searchTerm ? 'No options found' : 'No options available'}</span>
                        </div>
                    )}
                    
                    {!loading && sortedOptions.length > 0 && (
                        <div className="dropdown-options">
                            {/* Select All option for multiple selection with 10+ options */}
                            {multiple && sortedOptions.length >= 10 && (
                                <div className="dropdown-select-all">
                                    <div className="select-all-buttons">
                                        <button
                                            type="button"
                                            className="select-all-btn select-all"
                                            onClick={handleSelectAll}
                                            title="Select all options"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            className="select-all-btn deselect-all"
                                            onClick={handleDeselectAll}
                                            title="Deselect all options"
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {sortedOptions.map((option, index) => {
                                const isSelected = multiple 
                                    ? selectedValues.includes(option[optionValue])
                                    : value === option[optionValue];
                                const isFocused = index === focusedIndex;
                                
                                return (
                                    <div
                                        key={option[optionValue]}
                                        className={`dropdown-option ${isSelected ? 'selected' : ''} ${isFocused ? 'focused' : ''}`}
                                        onClick={() => handleOptionSelect(option)}
                                        onMouseEnter={() => setFocusedIndex(index)}
                                    >
                                        <span className="option-label">
                                            {option[optionLabel]}
                                        </span>
                                        {isSelected && (
                                            <span className="option-checkmark">✓</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
